import { IconCheck } from '@developerz/ui';
import { createEffect, createSignal, Index, type JSX, on, Show } from 'solid-js';
import { t } from '../../i18n';
import type { CardAnswer } from '../../lib/card-answer';
import type { CardState, MissionView } from '../../lib/machine-card';
import { BlockerRow } from './BlockerRow';
import { EpicRow } from './EpicRow';
import { MoveNote } from './MoveNote';

// Mission (#6785, prototype progressHTML and the Mission label, `:2462-2463`): up to three
// epic rows, then "+N more epics" (a view control, not a write), and the open blockers on
// the current task under their repo. The label counts the rows that need a person. With no
// epics reported (`null`, today, until #6745) and no blocker the card omits the zone, so
// nothing here ever claims "No epic assigned" for a job whose epic is simply not reported.
// Rows are by position (`<Index>`): a poll hands new row objects, and a keyed walk would
// rebuild a row, and its focused "Open blocker" link, every 20 seconds.
//
// THE ANSWER NOTE (#6726, prototype qaNoteHTML, `:1466-1470`) says what an "Answer here"
// did, for six seconds. Its words are a `role="status"` region; focus goes to the note's
// NON-LIVE wrapper, and only when focus was dropped (the answered box that held it has just
// closed), never off a box the person is typing in. It outlives the row it answered:
// `mission` is null once the re-read drops a card's only blocker, and the card keeps this
// zone up for the note. As it expires, the card hands focus on it back to the card's name.

const SHOWN = 3;

const NO_MISSION: MissionView = {
  epics: [],
  noEpic: false,
  epicCount: null,
  needsHuman: null,
  blockers: [],
};

export function MissionZone(props: {
  readonly mission: MissionView | null;
  readonly answer: CardAnswer;
  /** The card's state: an epic's controls show only on an Autonomous or Needs you machine. */
  readonly state: CardState;
}): JSX.Element {
  const [all, setAll] = createSignal(false);
  const m = (): MissionView => props.mission ?? NO_MISSION;
  let noteEl: HTMLDivElement | undefined;
  createEffect(
    on(
      () => props.answer.note(),
      (note) => {
        const at = document.activeElement;
        if (note !== null && (at === null || at === document.body)) noteEl?.focus();
      },
      { defer: true },
    ),
  );
  const shown = () => (all() ? m().epics : m().epics.slice(0, SHOWN));
  return (
    <section
      class="mission-zone"
      aria-label={t('fleet.machines.card_mission')}
      data-move-zone="mission"
    >
      <p class="mission-zone__label">
        <span>{t('fleet.machines.card_mission')}</span>
        <span class="mission-zone__side">
          <Show when={m().epicCount}>{(count) => <span>{count()}</span>}</Show>
          <Show when={m().needsHuman}>
            {(count) => <b class="mission-zone__needs">{count()}</b>}
          </Show>
        </span>
      </p>
      <Show when={m().noEpic}>
        <p class="mission-zone__quiet">{t('fleet.machines.card_no_epic')}</p>
      </Show>
      <Index each={shown()}>{(epic) => <EpicRow epic={epic()} state={props.state} />}</Index>
      <Show when={m().epics.length > SHOWN}>
        <button type="button" class="mission-zone__toggle" onClick={() => setAll((v) => !v)}>
          {all()
            ? t('fleet.machines.card_epic_fewer')
            : t('fleet.machines.card_epic_more', { count: m().epics.length - SHOWN })}
        </button>
      </Show>
      <Index each={m().blockers}>
        {(blocker) => <BlockerRow blocker={blocker()} answer={props.answer} />}
      </Index>
      <MoveNote zone="mission" />
      <Show when={props.answer.note()}>
        {(note) => (
          <div ref={noteEl} class="mission-zone__note" tabindex="-1">
            <IconCheck class="mission-zone__note-mark" />
            <p role="status">{note()}</p>
          </div>
        )}
      </Show>
    </section>
  );
}
